import { HiCheck } from "react-icons/hi";

// diferenças principais: items-center, min-h por size, shrink-0 e left offset por size
const cx = (...c) => c.filter(Boolean).join(" ");

export default function CustomSwitch({
  checked = false,
  onChange,
  disabled = false,
  label,
  subtitle,
  size = "md",
  id,
  className,
  showIcons = true,
  color = "blue",
}) {
  const sizes = {
    sm: {
      track: "w-10 h-5",
      thumb: "w-4 h-4",
      translate: "translate-x-4",
      pad: "p-0.5",
      minH: "min-h-[20px]",
      left: "left-1",
      text: "text-[8px]",
    },
    md: {
      track: "w-12 h-6",
      thumb: "w-5 h-5",
      translate: "translate-x-5",
      pad: "p-0.5",
      minH: "min-h-[24px]",
      left: "left-1",
      text: "text-[9px]",
    },
    lg: {
      track: "w-14 h-7",
      thumb: "w-6 h-6",
      translate: "translate-x-6",
      pad: "p-0.5",
      minH: "min-h-[28px]",
      left: "left-1",
      text: "text-[10px]",
    },
  };
  const s = sizes[size] || sizes.md;

  const activeBg =
    {
      blue: "from-blue-600 to-indigo-600",
      emerald: "from-emerald-600 to-teal-600",
      violet: "from-violet-600 to-fuchsia-600",
      rose: "from-rose-600 to-pink-600",
    }[color] || "from-blue-600 to-indigo-600";

  const ringColor =
    {
      blue: "ring-blue-500 dark:ring-blue-400",
      emerald: "ring-emerald-500 dark:ring-emerald-400",
      violet: "ring-violet-500 dark:ring-violet-400",
      rose: "ring-rose-500 dark:ring-rose-400",
    }[color] || "ring-blue-500 dark:ring-blue-400";

  return (
    <label
      htmlFor={id}
      className={cx(
        // alinhamento vertical centralizado
        "flex items-center gap-3 select-none",
        s.minH, // garante altura mínima do container = altura do track
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        className
      )}
    >
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        aria-label={typeof label === "string" ? label : undefined}
        onClick={() => !disabled && onChange?.(!checked)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onChange?.(!checked);
          }
        }}
        className={cx(
          "relative rounded-full transition-all outline-none shrink-0", // <- impede “esticamento”
          "focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-0",
          `focus-visible:${ringColor}`,
          s.track,
          s.pad,
          "border border-gray-300/70 dark:border-gray-700/60",
          "shadow-[inset_0_0_0_1px_rgba(255,255,255,0.35)] dark:shadow-[inset_0_0_0_1px_rgba(0,0,0,0.35)]",
          !disabled && "hover:brightness-[1.02]",
          checked
            ? `bg-gradient-to-r ${activeBg}`
            : "bg-gray-200/80 dark:bg-gray-800"
        )}
      >
        {showIcons && (
          <>
            <span
              className={cx(
                "absolute left-2 top-1/2 -translate-y-1/2 font-semibold tracking-wide",
                s.text,
                checked ? "text-white/90 opacity-100" : "opacity-0",
                "transition-opacity"
              )}
            >
              <HiCheck fontSize={14} />
            </span>
            <span
              className={cx(
                "absolute right-2 top-1/2 -translate-y-1/2 font-semibold tracking-wide",
                s.text,
                checked ? "opacity-0" : "opacity-70",
                "text-gray-600 dark:text-gray-300 transition-opacity"
              )}
            ></span>
          </>
        )}

        {/* thumb centralizado com offset por size */}
        <span
          className={cx(
            "relative z-[1] block rounded-full bg-white dark:bg-gray-100",
            "shadow-[0_2px_10px_rgba(0,0,0,0.25)]",
            "ring-1 ring-black/5 dark:ring-white/10",
            "transition-transform duration-300 ease-out will-change-transform",
            "top-1/2 -translate-y-1/2", // garante centralização vertical
            s.left, // offset inicial consistente
            s.thumb,
            checked ? s.translate : "-translate-x-1"
          )}
        >
          <span
            className="pointer-events-none absolute inset-0 rounded-full bg-white/50 mix-blend-overlay"
            aria-hidden="true"
          />
        </span>
      </button>

      {(label || subtitle) && (
        <div className="leading-tight">
          {label && (
            <div className="text-sm font-semibold text-slate-700 dark:text-gray-100">
              {label}
            </div>
          )}
          {subtitle && (
            <div className="text-xs text-slate-500 dark:text-gray-400">
              {subtitle}
            </div>
          )}
        </div>
      )}
    </label>
  );
}
